/**
 * A persistent toast utility that keeps a single toast element on screen
 * and updates its message in-place without creating new toast animations.
 */

type ToastVariant = 'success' | 'info' | 'warning' | 'error';

const VARIANT_COLORS: Record<ToastVariant, { bg: string; border: string; text: string }> = {
  success: { bg: '#eafbe7', border: '#47a53a', text: '#1e4d18' },
  info:    { bg: '#e8f4fd', border: '#2e75d6', text: '#1a3d6d' },
  warning: { bg: '#fef3cd', border: '#cc8b00', text: '#664600' },
  error:   { bg: '#fde8e8', border: '#d93025', text: '#6d1a1a' },
};

let toastElement: HTMLDivElement | null = null;
let autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

function getOrCreateToast(): HTMLDivElement {
  if (toastElement && document.body.contains(toastElement)) {
    return toastElement;
  }

  const el = document.createElement('div');
  el.id = 'persistent-toast';
  el.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    padding: 12px 20px;
    border-radius: 6px;
    border-left: 4px solid #2e75d6;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-width: 500px;
    min-width: 280px;
    text-align: center;
    transition: opacity 0.2s ease;
  `;
  document.body.appendChild(el);
  toastElement = el;
  return el;
}

/**
 * Show or update the persistent toast with a new message and variant.
 * Only one toast exists at a time; calling this again just changes the text and color.
 */
export function updateToast(message: string, variant: ToastVariant = 'info'): void {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  const el = getOrCreateToast();
  const colors = VARIANT_COLORS[variant];
  el.style.backgroundColor = colors.bg;
  el.style.borderLeftColor = colors.border;
  el.style.color = colors.text;
  el.textContent = message;
  el.style.opacity = '1';
}

/**
 * Show or update the toast, then auto-close it after the given duration (ms).
 */
export function showTemporaryToast(message: string, variant: ToastVariant = 'success', duration: number = 3000): void {
  updateToast(message, variant);

  autoCloseTimer = setTimeout(() => {
    closeToast();
  }, duration);
}

/**
 * Remove the persistent toast from the DOM.
 */
export function closeToast(): void {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }

  if (toastElement && document.body.contains(toastElement)) {
    toastElement.style.opacity = '0';
    setTimeout(() => {
      if (toastElement && document.body.contains(toastElement)) {
        document.body.removeChild(toastElement);
      }
      toastElement = null;
    }, 200);
  }
}
